import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	ConnectionAutoUpdate,
	DEFAULT_CONNECTION_AUTO_UPDATE_INTERVAL,
} from "@signalist/shared-types";

import { faRotate, faUpload } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { SelectField, SelectFieldOptions } from "@/components/SelectField/SelectField";
import { Toggle } from "@/components/Toggle/Toggle";

import { ConnectionScrapingInterval } from "@/enum/connection-scraping-interval.enum";

import {
	useSyncConnectionsMutation,
	useUpdateMeMutation,
} from "@/pages/Private/redux/account/account.api";

import { runOrToast } from "@/utils/runOrToast";

import { UploadConnectionsModal } from "./modals/UploadConnectionsModal";
import { SettingsSubhead, SettingsSubBlock } from "./SettingsSection";

interface LinkedinSyncSectionProps {
	/** The setting the scheduler acts on: `connectionAutoUpdateOf(user)` (PREC-4540). */
	autoUpdate: ConnectionAutoUpdate;
}

/**
 * "Connection syncing" section of the LinkedIn card: the auto-update toggle + interval, plus
 * on-demand "Sync now" and "Upload Connections.csv" actions. Reuses the same `updateMe`,
 * sync, and upload endpoints the old ConnectionScraping card used.
 *
 * The toggle shows what the cron does, and switching it on stores the flag and an interval
 * together, because the cron runs a user only when both are set (PREC-4540). "Sync now" and the
 * upload are manual, one-time and work whether auto-update is on or off; neither switches it on.
 * Copy: `syncHelp` plus `syncHelpAutoOn` / `syncHelpAutoOff` (en/deMcpConsent.ts).
 */
export const LinkedinSyncSection: FunctionComponent<LinkedinSyncSectionProps> = ({
	autoUpdate,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.connectedAccounts.${key}`);

	const [updateMe, { isLoading }] = useUpdateMeMutation();
	const [syncConnections, { isLoading: isSyncing }] = useSyncConnectionsMutation();

	const [showUploadModal, setShowUploadModal] = useState(false);

	const handleToggleChange = async () => {
		const updates = autoUpdate.enabled
			? { connectionScraping: false, connectionScrapingInterval: null }
			: {
					connectionScraping: true,
					connectionScrapingInterval:
						ConnectionScrapingInterval[DEFAULT_CONNECTION_AUTO_UPDATE_INTERVAL],
				};

		await runOrToast(() => updateMe(updates).unwrap(), t("errors.saveFailed"));
	};

	const handleIntervalChange = async (value: string | number | null) => {
		await runOrToast(
			() =>
				updateMe({
					connectionScrapingInterval: (value as ConnectionScrapingInterval) ?? null,
				}).unwrap(),
			t("errors.saveFailed")
		);
	};

	const handleSyncNow = async () => {
		await runOrToast(() => syncConnections().unwrap(), t("errors.syncFailed"));
	};

	const intervalOptions: SelectFieldOptions = [
		{ title: ts("intervalDaily"), value: ConnectionScrapingInterval.DAILY },
		{ title: ts("intervalWeekly"), value: ConnectionScrapingInterval.WEEKLY },
		{ title: ts("intervalMonthly"), value: ConnectionScrapingInterval.MONTHLY },
	];

	return (
		<div>
			<SettingsSubhead>{ts("syncingSubhead")}</SettingsSubhead>

			<SettingsSubBlock className="mt-2">
				<div className="flex items-start gap-3">
					<Toggle
						className="mt-0.5"
						disabled={isLoading}
						isChecked={autoUpdate.enabled}
						name="connection-scraping"
						testId="connection-scraping-toggle"
						onChange={handleToggleChange}
					/>

					<div className="min-w-0 flex-1">
						<span className="text-sm font-semibold text-text">{ts("autoUpdateLabel")}</span>
						<p className="mt-1.5 text-[12.5px] leading-relaxed text-text-meta">
							{ts("autoUpdateDesc")}
						</p>

						{autoUpdate.interval && (
							<div className="mt-4 max-w-[220px]">
								<SelectField
									// The settings card clips overflow, so the list renders outside it (PREC-4463).
									portal
									hideEmptyValue
									handleSelect={handleIntervalChange}
									label={ts("autoUpdateWindow")}
									options={intervalOptions}
									selectedOption={autoUpdate.interval}
								/>
							</div>
						)}
					</div>
				</div>

				<div className="mt-4 border-t border-gray-200 pt-4">
					{/* Automatic updates are promised only while the cron runs this user (PREC-4540). */}
					<p className="mb-3 text-[12.5px] leading-relaxed text-text-meta">
						<span>{ts("syncHelp")}</span>{" "}
						<span>{ts(autoUpdate.enabled ? "syncHelpAutoOn" : "syncHelpAutoOff")}</span>
					</p>
					<div className="flex flex-wrap gap-3">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2 h-3.5 w-3.5" icon={faRotate} />}
							isLoading={isSyncing || isLoading}
							size={ButtonSize.S}
							title={ts("syncNow")}
							type="button"
							onClick={handleSyncNow}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2 h-3.5 w-3.5" icon={faUpload} />}
							size={ButtonSize.S}
							title={ts("uploadConnections")}
							type="button"
							onClick={() => setShowUploadModal(true)}
						/>
					</div>
				</div>
			</SettingsSubBlock>

			<UploadConnectionsModal
				handleClose={() => setShowUploadModal(false)}
				isVisible={showUploadModal}
			/>
		</div>
	);
};
